import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { getDurationsAndPrice } from "@/Utilities";
import { IoIosWarning } from "react-icons/io";
import { api } from "@/lib/api-client";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import React, { useEffect, useRef, useState } from "react";
import { LuCopySlash, LuPuzzle, LuTimerOff } from "react-icons/lu";
import { useAppointmentModalStore } from "../useAppointmentModalStore_";
import {
  formatCurrency,
  returnCurrencySymbol,
} from "../../../../../shared/utils/utils";
import {
  AutocompleteItem,
  AutocompleteSection,
  Tooltip,
  useDisclosure,
  Divider,
  Button,
} from "@heroui/react";
import { LimeAutocomplete } from "@/Components/NextBase/LimeAutocomplete";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import {
  ArrowDown,
  Box,
  Boxes,
  CopySlash,
  Globe,
  Heart,
  Timer,
  TimerOff,
  TimerReset,
  Trash,
  TriangleAlert,
  Undo2,
  User,
  Users,
} from "lucide-react";
import { cn, humanizeDuration } from "@/utils";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeStepperNumberInput } from "@/Components/NextBase/LimeStepperNumberInput";
import { i18n } from "@lingui/core";
import {
  LimeFieldActionButton,
  LimeFieldActions,
} from "@/Components/NextBase/LimeFieldActions";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { useConfirmModal } from "@/Components/NextBase/useConfirmModal";
import { getDroppedRequestedUserIds } from "./requestedStaffChange";
import { DurationInput } from "@/Components/NextBase/DurationInput";
import useAccountInfo from "@/hooks/useAccountInfo";
import { useStaffRequestIndicatorVisible } from "@/hooks/useStaffRequestIndicator";
import { useClientFeature } from "@/hooks/useClientFeature";

const SelectedServiceListItem = ({
  selectedService,
  selectedServices,
  setSelectedServices,
  service,
  index,
  isNewAppointment,
  isPreScheduled,
  startDuration,
  hasOnlyOneInput,
  timeOffDuration,
  endDuration,
  defaultOpen,
  addons,
}) => {
  const [selectMultipleUsers, setSelectMultipleUsers] = useState(
    selectedService.users.length > 1,
  );
  const [selectMultipleResources, setSelectMultipleResources] = useState(true);

  const { appointmentData } = useAppointmentModalStore((s) => s);

  const { accountInfo } = useAccountInfo();
  const isStaffRequestIndicatorVisible = useStaffRequestIndicatorVisible();

  const isFirstRender = useRef(true);

  const currentServiceAddons = addons.filter(
    (addon) => addon.serviceId === selectedService.serviceId,
  );

  const {
    users,
    resources,
    services,
    doesHaveCombinations,
    selectedLocationId,
    calendarData,
    serviceTags,
    lastSelectedResourceId,
  } = useCalendarStore((s) => ({
    users: s.users,
    resources: s.resources,
    services: s.services,
    doesHaveCombinations: s.doesHaveCombinations,
    selectedLocationId: s.selectedLocationId,
    calendarData: s.calendarData,
    serviceTags: s.serviceTags,
    lastSelectedResourceId: s.lastSelectedResourceId,
  }));

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const { data: validOptions, mutateAsync: checkValidOptions } =
    api.appointments.usePostValidOptions();

  const { isFeatureEnabled } = useClientFeature();

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (
      !(
        doesHaveCombinations ||
        permissions.show_appointment_warnings ||
        permissions.restrict_appointment_overlaps ||
        permissions.restrict_appointment_services ||
        permissions.restrict_appointment_to_shifts
      )
    ) {
      return;
    }

    if (isFirstRender.current) {
      return;
    }

    checkValidOptions({
      selectedServices: selectedServices.map((s) => ({
        ...s,
        duration:
          (s.startDuration || 0) + (timeOffDuration || 0) + (endDuration || 0),
        timeOffDuration: s.timeOffDuration || undefined,
        timeOffStart: s.timeOffDuration ? s.startDuration : undefined,
      })),
      selectedServiceIndex: index,
      locationId: selectedLocationId,
      startTime: appointmentData.startTime,
    });
  }, [
    selectedService.users,
    selectedService.resources,
    selectedService.serviceId,
    selectedServices.length,
  ]);

  const usersDropdown = React.useMemo(
    () =>
      users.flatMap((user) => {
        if (
          validOptions &&
          !validOptions.availableUsers.find(
            (usr) => user.userId === usr.userId && usr.isValid,
          )
        )
          return [];

        const firstName = user.name?.toUpperCase();
        const lastName = user.lastName?.toUpperCase();

        const availableUserDetails = validOptions?.availableUsers?.find(
          (u) => u.userId === user.userId,
        );

        return [
          {
            id: user.userId,
            value: user.userId.toString(),
            fullName: `${user.name} ${user.lastName}`,
            label: `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`,
            ...availableUserDetails,
            isValid: availableUserDetails ? availableUserDetails.isValid : true,
          },
        ];
      }),
    [users, validOptions],
  );

  const servicesDropdown = React.useMemo(
    () =>
      services
        // keep deleted services out of the picker, except the one already
        // assigned to this appointment so it stays visible/selected
        .filter(
          (s) =>
            (!s.isDeleted || s.serviceId === selectedService.serviceId) &&
            (s.serviceId === selectedService.serviceId ||
              !validOptions ||
              validOptions.availableServices.find(
                (ser) => s.serviceId === ser.serviceId && ser.isValid,
              )),
        )
        // sort by serviceTags sortOrder first and then service sortOrder inside
        .toSorted((a, b) => {
          const aTagOrder =
            serviceTags.find((t) => t.tagId === a.tagId)?.sortOrder || 99;
          const bTagOrder =
            serviceTags.find((t) => t.tagId === b.tagId)?.sortOrder || 99;

          if (aTagOrder !== bTagOrder) return aTagOrder - bTagOrder;

          const aServiceOrder = a.sortOrder;
          const bServiceOrder = b.sortOrder;

          return aServiceOrder - bServiceOrder;
        })
        .map((service) => {
          const availableServiceDetails = validOptions?.availableServices?.find(
            (s) => s.serviceId === service.serviceId,
          );

          return {
            id: service.serviceId,
            value: service.serviceId.toString(),
            label: service.name,
            ...availableServiceDetails,
            isValid: availableServiceDetails
              ? availableServiceDetails.isValid
              : true,
            tagId: service.tagId,
            tagName: serviceTags.find((t) => t.tagId === service.tagId)
              ?.tagName,
          };
        }),
    [services, validOptions, serviceTags, selectedService.serviceId],
  );

  const resourcesDropdown = React.useMemo(
    () =>
      resources.flatMap((r) => {
        if (
          validOptions &&
          !validOptions.availableResources.find(
            (av) => r.id === av.resourceId && av.isValid,
          )
        )
          return [];

        const availableResourceDetails = validOptions?.availableResources?.find(
          (res) => res.resourceId === r.id,
        );

        return [
          {
            id: r.id,
            value: r.id.toString(),
            label: r.label,
            ...availableResourceDetails,
            capacityMax: r.capacityMax,
            isValid: availableResourceDetails
              ? availableResourceDetails.isValid
              : true,
          },
        ];
      }),
    [resources, validOptions],
  );

  useEffect(() => {
    if (
      doesHaveCombinations ||
      permissions.show_appointment_warnings ||
      permissions.restrict_appointment_overlaps ||
      permissions.restrict_appointment_services ||
      permissions.restrict_appointment_to_shifts
    ) {
      if (!validOptions) return;
    }

    if (
      selectedService.users.length > 0 &&
      selectedService.resources.length > 0
    )
      return;

    if (selectedService.appointmentId) {
      return;
    }

    const newServices = [...selectedServices];
    if (selectedService.users.length === 0 && service.doesRequireUser) {
      const freeUser = usersDropdown.find((user) => user.isValid);

      if (freeUser) {
        newServices[index].users = [Number(freeUser.value)];
        setSelectedServices(newServices);
      }
    }

    if (
      selectedService.resources.length === 0 &&
      service.serviceResourceGroups.length > 0
    ) {
      const freeResource = resourcesDropdown.find(
        (resource) => resource.isValid,
      );

      if (freeResource) {
        newServices[index].resources = [freeResource.id];
      }
    }

    if (service.isAttendanceExplicit) {
      newServices[index].attendance = newServices[index].attendance || 1;
    } else {
      newServices[index].attendance = undefined;
    }

    setSelectedServices(newServices);
  }, [selectedService.serviceId]);

  useEffect(() => {
    if (defaultOpen) {
      onOpen();
    }
  }, [defaultOpen]);

  const removeServiceFromLinked = () => {
    const newServices = [...selectedServices];
    if (selectedService.length <= 1) {
      return;
    }
    if (newServices[index].removed) {
      newServices[index].removed = false;
    } else {
      if (newServices[index].appointmentId) {
        newServices[index].removed = true;
      } else {
        newServices.splice(index, 1);
      }
    }

    setSelectedServices(newServices);
  };

  // Only meaningful once somebody is on the line: the server records nothing
  // for a resource-only part, so offering the toggle there would promise a
  // fact that never gets stored.
  const canRequestStaff =
    isStaffRequestIndicatorVisible && (selectedService.users?.length ?? 0) > 0;
  const isStaffRequested =
    isStaffRequestIndicatorVisible &&
    selectedService.customerRequestedStaff === true;
  const isRequestedOnline = selectedService.isStaffRequestedOnline === true;
  const isOnlinePickShown = isStaffRequested && isRequestedOnline;

  const staffRequestedLabel = isOnlinePickShown
    ? t`Stranka je tega izvajalca izbrala ob spletni rezervaciji`
    : t`Stranka je želela točno tega izvajalca`;
  const multipleUsersLabel = selectMultipleUsers
    ? t`Izberi enega zaposlenega`
    : t`Izberi več zaposlenih`;
  const multipleResourcesLabel = selectMultipleResources
    ? t`Izberi eno sredstvo`
    : t`Izberi več sredstev`;

  const setStaffRequested = (isRequested) => {
    const newSelectedServices = [...selectedServices];
    newSelectedServices[index].customerRequestedStaff = isRequested;
    newSelectedServices[index].isCustomerRequestedStaffEdited = true;
    // Turning it back on records the desk's word, not the customer's pick.
    newSelectedServices[index].isStaffRequestedOnline = false;
    setSelectedServices(newSelectedServices, false, false);
  };

  const {
    open: openRemoveOnlineRequestConfirm,
    modal: removeOnlineRequestConfirmModal,
  } = useConfirmModal({
    title: t`Odstranitev želenega izvajalca`,
    content: (
      <p>
        {t`Stranka je tega izvajalca izbrala sama ob spletni rezervaciji. Ste prepričani, da želite odstraniti oznako?`}
      </p>
    ),
    confirmProps: {
      label: t`Odstrani`,
      dataIdentifier: "confirm-remove-online-requested-staff",
      isDestructive: true,
      onClick: async () => setStaffRequested(false),
    },
  });

  const toggleStaffRequested = () => {
    if (isOnlinePickShown) {
      openRemoveOnlineRequestConfirm();
      return;
    }

    setStaffRequested(!isStaffRequested);
  };

  // The recorded request does not name anyone, so it is measured against the
  // people who were on the line when it was booked. Dropping any of them is
  // the edit worth interrupting; adding a second employee alongside them is
  // not, because the requested one still serves the customer.
  const requestedUserIds = selectedService.requestedUserIds ?? [];

  const droppedRequestedUserIds = (nextUserIds) =>
    getDroppedRequestedUserIds({
      requestedUserIds,
      nextUserIds,
      isRequestRecorded: !isNewAppointment && isStaffRequested,
    });

  const applyUserChange = ({ userIds, overridePrice }) => {
    const newSelectedServices = [...selectedServices];
    newSelectedServices[index].users = userIds;
    // Emptying the line hides the heart, so a flag left set here would come
    // back on unseen against whoever is picked next.
    if (userIds.length === 0) {
      newSelectedServices[index].customerRequestedStaff = false;
    }
    setSelectedServices(newSelectedServices, overridePrice);
  };

  // Held while the confirm dialog is open. The select stays controlled by
  // `selectedServices`, so cancelling needs no revert — nothing was written.
  const [pendingUserChange, setPendingUserChange] = useState(null);

  const changeUsers = (change) => {
    if (droppedRequestedUserIds(change.userIds).length === 0) {
      if (change.selectMultipleUsers != null) {
        setSelectMultipleUsers(change.selectMultipleUsers);
      }
      applyUserChange(change);
      return;
    }

    setPendingUserChange(change);
    openRequestedStaffConfirm();
  };

  const droppedRequestedUserNames = (
    pendingUserChange ? droppedRequestedUserIds(pendingUserChange.userIds) : []
  ).map((userId) => {
    const user = users.find((u) => u.userId === userId);
    return user
      ? `${user.name ?? ""} ${user.lastName ?? ""}`.trim()
      : t`Neznan izvajalec`;
  });

  const { open: openRequestedStaffConfirm, modal: requestedStaffConfirmModal } =
    useConfirmModal({
      title: t`Zamenjava želenega izvajalca`,
      content: (
        <p>
          {(() => {
            const names = droppedRequestedUserNames.join(", ");
            if (isRequestedOnline) {
              return droppedRequestedUserNames.length > 1
                ? t`Stranka je ob spletni rezervaciji izbrala točno te izvajalce: ${names}. Ste prepričani, da jih želite zamenjati?`
                : t`Stranka je ob spletni rezervaciji izbrala točno tega izvajalca: ${names}. Ste prepričani, da ga želite zamenjati?`;
            }

            return droppedRequestedUserNames.length > 1
              ? t`Stranka je želela točno te izvajalce: ${names}. Ste prepričani, da jih želite zamenjati?`
              : t`Stranka je želela točno tega izvajalca: ${names}. Ste prepričani, da ga želite zamenjati?`;
          })()}
        </p>
      ),
      confirmProps: {
        label: t`Zamenjaj`,
        dataIdentifier: "confirm-requested-staff-change",
        onClick: async () => {
          if (!pendingUserChange) return;
          if (pendingUserChange.selectMultipleUsers != null) {
            setSelectMultipleUsers(pendingUserChange.selectMultipleUsers);
          }
          applyUserChange(pendingUserChange);
          setPendingUserChange(null);
        },
      },
      cancelProps: {
        onClick: () => setPendingUserChange(null),
      },
    });

  const toggleSelectMultipleUsers = (active) => {
    //  if multiple users selected, remove all users except the first one
    if (!active) {
      changeUsers({
        userIds:
          selectedService.users[0] != null ? [selectedService.users[0]] : [],
        overridePrice: true,
        selectMultipleUsers: false,
      });
      return;
    }

    setSelectMultipleUsers(active);
  };
  const toggleSelectMultipleResources = (active) => {
    setSelectMultipleResources(active);

    // if multiple resources selected, remove all resources except the first one
    if (!active) {
      const newSelectedServices = [...selectedServices];
      newSelectedServices[index].resources = selectedService.resources[0]
        ? [selectedService.resources[0]]
        : [];
      setSelectedServices(newSelectedServices);
    }
  };

  const notEnoughResourcesWarning = (() => {
    if (!service.isAttendanceExplicit) return false;
    if (!service.serviceResourceGroups.length) return false;

    const attendance = selectedService.attendance;
    const resourcesMaxCapacity = selectedService.resources.reduce(
      (acc, resource) => {
        const currResource = resources.find((r) => r.id === resource);

        if (!currResource) return acc;

        return acc + currResource.capacityMax;
      },
      0,
    );

    return attendance > resourcesMaxCapacity;
  })();

  const discountedPrice = React.useMemo(() => {
    if (!selectedService.discountAmount) return selectedService.price || 0;

    if (selectedService.discountType === "amount") {
      return selectedService.price - selectedService.discountAmount;
    }

    return (
      selectedService.price -
      (selectedService.price * selectedService.discountAmount) / 100
    );
  }, [
    selectedService.discountAmount,
    selectedService.discountType,
    selectedService.price,
  ]);

  const discountInputError = React.useMemo(() => {
    if (!selectedService.discountAmount) return false;

    if (selectedService.discountType === "amount") {
      if (selectedService.discountAmount > selectedService.price) {
        return t`Popust ne sme biti večji od cene`;
      }

      if (selectedService.discountAmount < 0) {
        return t`Popust ne sme biti manjši od 0`;
      }
    }

    if (selectedService.discountType === "percentage") {
      if (selectedService.discountAmount > 100) {
        return t`Popust ne sme biti večji od 100`;
      }

      if (selectedService.discountAmount < 0) {
        return t`Popust ne sme biti manjši od 0`;
      }
    }
  }, [
    selectedService.discountAmount,
    selectedService.discountType,
    selectedService.price,
  ]);

  useEffect(() => {
    if (selectedServices?.length === 1) onOpen();

    async function getWarningsAndPickResources() {
      if (lastSelectedResourceId && index === 0) {
        isFirstRender.current = false;

        const newSelectedServices = [...selectedServices];
        newSelectedServices[index].resources = [lastSelectedResourceId];
        setSelectedServices(newSelectedServices);
        return;
      }

      const skipValidOptions = !(
        doesHaveCombinations ||
        permissions.show_appointment_warnings ||
        permissions.restrict_appointment_overlaps ||
        permissions.restrict_appointment_services ||
        permissions.restrict_appointment_to_shifts
      );

      const options = skipValidOptions
        ? undefined
        : await checkValidOptions({
            selectedServices: selectedServices.map((s) => ({
              ...s,
              duration:
                (s.startDuration || 0) +
                (timeOffDuration || 0) +
                (endDuration || 0),
              timeOffDuration: s.timeOffDuration || undefined,
              timeOffStart: s.timeOffDuration ? s.startDuration : undefined,
            })),
            selectedServiceIndex: index,
            locationId: selectedLocationId,
            startTime: appointmentData.startTime,
          });

      const selectedResourceIds = pickResources(options);

      isFirstRender.current = false;

      const newSelectedServices = [...selectedServices];
      newSelectedServices[index].resources = selectedResourceIds;
      setSelectedServices(newSelectedServices);
    }

    function pickResources(options) {
      const selectedResourceIds = [];
      const serviceLogic =
        service.serviceResourceGroupSetLogic === "OR" ? "some" : "every";
      service.ServiceResourceGroupSet[serviceLogic]((srgs) => {
        const resourceSelection = [];
        const setLogic = srgs.logicType === "OR" ? "some" : "every";

        const isSetAvailable = srgs.ServiceResourceGroupSetItem[setLogic](
          (srgsi) => {
            const resourcesForGroup = resources.filter(
              (r) => r.resourceGroupId === srgsi.resourceGroupId,
            );

            const pickedResource = resourcesForGroup.find((r) => {
              const resourceWarnings = options
                ? options.availableResources.find(
                    (rsc) => rsc.resourceId === r.id,
                  )
                : null;
              const isAvailable = r.isAvailable;

              if (!resourceWarnings) return isAvailable;

              const isOverlapping = resourceWarnings.resourceOverlapWarning;

              // check only if resource is overlapping, if shift check is needed add condition here
              return isAvailable && !isOverlapping;
            });

            if (!pickedResource) return false;

            resourceSelection.push(pickedResource.id);

            return true;
          },
        );

        if (isSetAvailable) {
          selectedResourceIds.push(...resourceSelection);
          return true;
        }

        return false;
      });

      return selectedResourceIds;
    }

    if (!selectedService.appointmentId) {
      getWarningsAndPickResources();
    }
  }, []);

  const _selectedService = services?.find(
    (s) => s.serviceId == selectedService.serviceId,
  );

  const showComplexDurationPicker = !!accountInfo.showComplexDurationPicker;

  const finalDurations = (() => {
    // Don't include addon durations for prescheduled appointments
    const { addonsStartDuration, addonsEndDuration } = isPreScheduled
      ? { addonsStartDuration: 0, addonsEndDuration: 0 }
      : currentServiceAddons.reduce(
          (acc, addon) => {
            if (addon.durationAdjustmentPosition === "start") {
              acc.addonsStartDuration += addon.durationMinsAdjustment;
            } else if (addon.durationAdjustmentPosition === "end") {
              acc.addonsEndDuration += addon.durationMinsAdjustment;
            }

            return acc;
          },
          { addonsStartDuration: 0, addonsEndDuration: 0 },
        );

    if (selectedService.timeOffDuration == null) {
      return {
        total:
          selectedService.startDuration +
          addonsStartDuration +
          addonsEndDuration,
        start:
          selectedService.startDuration +
          addonsStartDuration +
          addonsEndDuration,
        timeOffDuration: null,
        end: null,
      };
    }

    return {
      total:
        selectedService.startDuration +
        (selectedService.timeOffDuration || 0) +
        (selectedService.endDuration || 0) +
        addonsStartDuration +
        addonsEndDuration,
      start:
        selectedService.startDuration != null
          ? selectedService.startDuration + addonsStartDuration
          : null,
      timeOffDuration:
        selectedService.timeOffDuration != null
          ? selectedService.timeOffDuration
          : null,
      end:
        selectedService.endDuration != null
          ? selectedService.endDuration + addonsEndDuration
          : null,
    };
  })();

  return (
    <div
      style={{
        background: selectedService.removed
          ? "#ff000010"
          : "rgba(0, 0, 0, 0.02)",
        padding: "5px 7px",
        borderRadius: "10px",
      }}
    >
      {requestedStaffConfirmModal}
      {removeOnlineRequestConfirmModal}

      <div className="flex flex-col items-start justify-between">
        {/**
         * service combobox
         */}

        <Button
          onPress={isOpen ? onClose : onOpen}
          variant={isOpen ? "flat" : "light"}
          className="flex w-full items-center justify-between gap-1 pl-2 text-left"
        >
          <div className="ml-2 flex flex-col gap-0">
            <span className="text-sm font-medium">
              {_selectedService.name.length > 40
                ? _selectedService.name.slice(0, 40) + "..."
                : _selectedService.name}
            </span>
            <div className="flex items-center gap-1 text-xs font-normal text-gray-500">
              {finalDurations.start != null && (
                <>
                  <Timer size={12} className="inline" />
                  {humanizeDuration(finalDurations.start * 60000)}
                </>
              )}
              {finalDurations.timeOffDuration != null && (
                <>
                  {finalDurations.start != null ? ` ->` : undefined}{" "}
                  <TimerOff size={12} className="inline" />
                  {humanizeDuration(finalDurations.timeOffDuration * 60000)}
                </>
              )}
              {finalDurations.end != null && (
                <>
                  {finalDurations.timeOffDuration != null ||
                  finalDurations.start != null
                    ? ` ->`
                    : undefined}{" "}
                  <TimerReset size={12} className="inline" />
                  {humanizeDuration(finalDurations.end * 60000)}
                </>
              )}
            </div>
          </div>

          {selectedService.removed ? (
            <Button
              onPress={removeServiceFromLinked}
              variant={"light"}
              color="danger"
              isIconOnly
            >
              <Undo2 size={16} />
            </Button>
          ) : (
            <ArrowDown
              size={16}
              className={cn("transition-all", {
                "-rotate-180": isOpen,
              })}
            />
          )}
        </Button>

        <LimeCollapse isOpen={!selectedService.removed && isOpen} w={"100%"}>
          <div className="flex flex-col gap-2 py-2">
            <LimeAutocomplete
              isVirtualized={false} // TODO: set to true after bugs are fixed (https://github.com/heroui-inc/heroui/issues/4494) , (https://github.com/heroui-inc/heroui/issues/4568)
              selectedKey={selectedService?.serviceId?.toString()}
              size={"sm"}
              placeholder={t`Storitev še ni izbrana`}
              label={t`Storitev`}
              variant="faded"
              listBoxProps={{
                itemClasses: {
                  base: "h-fit w-fit",
                },
              }}
              scrollShadowProps={{
                isEnabled: false,
              }}
              onSelectionChange={(key) => {
                const val = Number(key);

                const newSelectedServices = [...selectedServices];

                const newService = services.find(
                  (service) => service.serviceId === val,
                );

                if (!newService) return;

                const userId =
                  selectedService.users[0] ?? selectedService.userId;
                const user = newService.users.find(
                  (user) => user.userId === userId,
                );

                const { startDuration, timeOffDuration, endDuration, price } =
                  getDurationsAndPrice(newService, user);

                newSelectedServices[index].serviceId = val;
                newSelectedServices[index].price = price;
                newSelectedServices[index].startDuration = startDuration;
                newSelectedServices[index].timeOffDuration = timeOffDuration;
                newSelectedServices[index].endDuration = endDuration;

                setSelectedServices(newSelectedServices);
              }}
            >
              {serviceTags
                ?.sort((a, b) => a.sortOrder - b.sortOrder)
                .map((st) => {
                  const services =
                    st.tagId === -1
                      ? servicesDropdown.filter((s) => !s.tagId)
                      : servicesDropdown.filter((s) => s.tagId === st.tagId);

                  if (services?.length === 0 && st.tagId !== -1) return null;

                  return (
                    <AutocompleteSection
                      classNames={{
                        heading:
                          "flex w-full sticky top-1 z-20 py-1.5 px-2 bg-default-100 rounded-small",
                      }}
                      showDivider
                      title={st.tagName}
                    >
                      {services?.map((s) => (
                        <AutocompleteItem
                          key={s.id.toString()}
                          textValue={s.label}
                          endContent={
                            <div className="flex items-center gap-2">
                              {s.userOverlapWarning ? (
                                <Tooltip
                                  content={t`Zaposleni je zaseden`}
                                  withArrow={true}
                                >
                                  <div>
                                    <LuCopySlash color="var(--error)"></LuCopySlash>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}
                              {s.resourceOverlapWarning ? (
                                <Tooltip
                                  content={t`Sredstvo je zasedeno`}
                                  withArrow={true}
                                >
                                  <div>
                                    <LuCopySlash color="var(--error)"></LuCopySlash>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}

                              {s.userShiftWarning ? (
                                <Tooltip
                                  content={t`Zaposleni nima urnika`}
                                  withArrow={true}
                                >
                                  <div>
                                    <LuTimerOff color="var(--error)"></LuTimerOff>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}
                              {s.resourceShiftWarning ? (
                                <Tooltip
                                  content={t`Sredstvo nima urnika`}
                                  withArrow={true}
                                >
                                  <div>
                                    <LuTimerOff color="var(--error)"></LuTimerOff>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}

                              {s.userRelationWarning ? (
                                <Tooltip
                                  content={t`Zaposleni ne izvaja te storitve`}
                                  withArrow={true}
                                >
                                  <div>
                                    <IoIosWarning color="var(--error)"></IoIosWarning>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}
                              {s.resourceRelationWarning ? (
                                <Tooltip
                                  content={t`Sredstvo ne izvaja te storitve`}
                                  withArrow={true}
                                >
                                  <div>
                                    <IoIosWarning color="var(--error)"></IoIosWarning>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}

                              {s.combinationWarning ? (
                                <Tooltip
                                  content={t`Storitev ni na voljo v kombinaciji z ostalimi izbranimi storitvami.`}
                                  withArrow={true}
                                >
                                  <div>
                                    <LuPuzzle color="var(--error)"></LuPuzzle>
                                  </div>
                                </Tooltip>
                              ) : (
                                ""
                              )}
                            </div>
                          }
                        >
                          {s.label}
                        </AutocompleteItem>
                      ))}
                    </AutocompleteSection>
                  );
                })}
            </LimeAutocomplete>

            {/**
             * resource combobox
             */}
            {isFeatureEnabled("resources") && (
              <>
                <LimeFieldActions
                  className={cn({ "mt-2": service.isAttendanceExplicit })}
                  actions={
                    <LimeFieldActionButton
                      label={multipleResourcesLabel}
                      isSelected={selectMultipleResources}
                      icon={
                        selectMultipleResources ? (
                          <Boxes size={18} />
                        ) : (
                          <Box size={18} />
                        )
                      }
                      onPress={() =>
                        toggleSelectMultipleResources(!selectMultipleResources)
                      }
                      dataIdentifier="select-multi-resources-toggle"
                    />
                  }
                >
                  <LimeSelect
                    data-identifier="appointment-resource-combobox"
                    selectionMode={
                      selectMultipleResources ? "multiple" : "single"
                    }
                    selectedKeys={
                      selectedServices[index].resources?.map(String) || []
                    }
                    onSingleItemChange={(val) => {
                      const newSelectedServices = [...selectedServices];

                      newSelectedServices[index].resources = val
                        ? [Number(val)]
                        : [];
                      setSelectedServices(newSelectedServices, true, false);
                    }}
                    onMultipleItemChange={(val) => {
                      const newSelectedServices = [...selectedServices];
                      newSelectedServices[index].resources = val.map(Number);
                      setSelectedServices(newSelectedServices, true, false);
                    }}
                    disallowEmptySelection={false}
                    items={
                      resourcesDropdown?.map((r) => {
                        const userOverlapWarning = r.userOverlapWarning ? (
                          <CopySlash size={14} color="var(--error)" />
                        ) : undefined;
                        const resourceOverlapWarning =
                          r.resourceOverlapWarning ? (
                            <CopySlash size={14} color="var(--error)" />
                          ) : undefined;
                        const userShiftWarning = r.userShiftWarning ? (
                          <TimerOff size={14} color="var(--error)" />
                        ) : undefined;
                        const resourceShiftWarning = r.resourceShiftWarning ? (
                          <TimerOff size={14} color="var(--error)" />
                        ) : undefined;
                        const userRelationWarning = r.userRelationWarning ? (
                          <TriangleAlert size={14} color="var(--error)" />
                        ) : undefined;
                        const resourceRelationWarning =
                          r.resourceRelationWarning ? (
                            <TriangleAlert size={14} color="var(--error)" />
                          ) : undefined;

                        return {
                          key: r.id.toString(),
                          label: r.label,
                          endContent: (
                            <>
                              {userOverlapWarning}
                              {resourceOverlapWarning}
                              {userShiftWarning}
                              {resourceShiftWarning}
                              {userRelationWarning}
                              {resourceRelationWarning}
                            </>
                          ),
                        };
                      }) ?? []
                    }
                    label={t`Sredstva`}
                    error={
                      notEnoughResourcesWarning
                        ? t`Za to število oseb potrebujete več sredstev`
                        : undefined
                    }
                    size="sm"
                  />
                </LimeFieldActions>

                {service.isAttendanceExplicit ? (
                  <LimeNumberInput
                    value={selectedService.attendance}
                    label={t`Število oseb`}
                    onValueChange={(val) => {
                      const newSelectedServices = [...selectedServices];
                      newSelectedServices[index].attendance = val;

                      setSelectedServices(newSelectedServices, true, false);
                    }}
                    step={1}
                    minValue={1}
                    size="sm"
                    hideStepper
                    labelPlacement="outside-left"
                    className="mb-2 justify-end self-end"
                  ></LimeNumberInput>
                ) : null}
              </>
            )}

            {/**
             * user combobox
             */}

            <LimeFieldActions
              actions={
                <>
                  {canRequestStaff ? (
                    <LimeFieldActionButton
                      label={staffRequestedLabel}
                      isSelected={isStaffRequested}
                      color="danger"
                      icon={
                        <span className="relative flex">
                          <Heart
                            size={18}
                            fill={isStaffRequested ? "currentColor" : "none"}
                          />
                          {isOnlinePickShown ? (
                            <Globe
                              size={11}
                              strokeWidth={2.5}
                              className="absolute -right-1.5 -bottom-1 rounded-full bg-content1"
                              data-identifier="customer-requested-staff-online"
                            />
                          ) : null}
                        </span>
                      }
                      onPress={toggleStaffRequested}
                      dataIdentifier="customer-requested-staff-toggle"
                    />
                  ) : null}

                  <LimeFieldActionButton
                    label={multipleUsersLabel}
                    isSelected={selectMultipleUsers}
                    icon={
                      selectMultipleUsers ? (
                        <Users size={18} />
                      ) : (
                        <User size={18} />
                      )
                    }
                    onPress={() =>
                      toggleSelectMultipleUsers(!selectMultipleUsers)
                    }
                    dataIdentifier="select-multi-users-toggle"
                  />
                </>
              }
            >
              <LimeSelect
                data-identifier="appointment-user-combobox"
                selectionMode={selectMultipleUsers ? "multiple" : "single"}
                selectedKeys={selectedServices[index].users?.map(String) || []}
                onSingleItemChange={(val) => {
                  changeUsers({
                    userIds: val ? [Number(val)] : [],
                    overridePrice: isNewAppointment,
                  });
                }}
                onMultipleItemChange={(val) => {
                  changeUsers({
                    userIds: val.length > 0 ? val.map(Number) : [],
                    overridePrice: isNewAppointment,
                  });
                }}
                disallowEmptySelection={false}
                items={
                  usersDropdown?.map((u) => {
                    const userOverlapWarning = u.userOverlapWarning ? (
                      <CopySlash size={14} color="var(--error)" />
                    ) : undefined;
                    const userShiftWarning = u.userShiftWarning ? (
                      <TimerOff size={14} color="var(--error)" />
                    ) : undefined;
                    const userRelationWarning = u.userRelationWarning ? (
                      <TriangleAlert size={14} color="var(--error)" />
                    ) : undefined;
                    const resourceOverlapWarning = u.resourceOverlapWarning ? (
                      <CopySlash size={14} color="var(--error)" />
                    ) : undefined;
                    const resourceShiftWarning = u.resourceShiftWarning ? (
                      <TimerOff size={14} color="var(--error)" />
                    ) : undefined;
                    const resourceRelationWarning =
                      u.resourceRelationWarning ? (
                        <TriangleAlert size={14} color="var(--error)" />
                      ) : undefined;

                    return {
                      key: u.id.toString(),
                      label: u.fullName,
                      endContent: (
                        <>
                          {userOverlapWarning}
                          {userShiftWarning}
                          {userRelationWarning}
                          {resourceOverlapWarning}
                          {resourceShiftWarning}
                          {resourceRelationWarning}
                        </>
                      ),
                    };
                  }) ?? []
                }
                label={t`Zaposleni`}
                size="sm"
              />
            </LimeFieldActions>

            <>
              {!isPreScheduled && (
                <>
                  <Divider />

                  <div>
                    <div className="w-44">
                      <LimeStepperNumberInput
                        value={selectedService.price || 0}
                        onValueChange={(val) => {
                          const newSelectedServices = [...selectedServices];
                          newSelectedServices[index].price = val;

                          setSelectedServices(
                            newSelectedServices,
                            false,
                            false,
                          );
                        }}
                        label={t`Cena`}
                        min={0}
                        size="sm"
                        endContent={
                          <p className="text-sm">
                            {returnCurrencySymbol({
                              currency: calendarData.currency || "EUR",
                            })}
                          </p>
                        }
                        steppersProps={{
                          variant: "flat",
                        }}
                      />
                    </div>

                    <div className="mt-1">
                      <LimeNumberInput
                        data-identifier="discount-input"
                        label={t`Popust`}
                        size="sm"
                        value={selectedService.discountAmount ?? 0}
                        onValueChange={(val) => {
                          const newSelectedServices = [...selectedServices];
                          newSelectedServices[index].discountAmount = val;

                          setSelectedServices(
                            newSelectedServices,
                            false,
                            false,
                          );
                        }}
                        // € discounts are stored in cents, % discounts as whole numbers
                        step={
                          selectedService.discountType === "amount" ? 0.01 : 1
                        }
                        minValue={0}
                        error={discountInputError}
                        isDisabled={
                          !!permissions.restrict_custom_price ||
                          !permissions.manage_appointments
                        }
                        endContent={
                          <Button
                            isIconOnly
                            variant="ghost"
                            size="sm"
                            onPress={() => {
                              const newSelectedServices = [...selectedServices];
                              newSelectedServices[index].discountType =
                                selectedService.discountType === "amount"
                                  ? "percentage"
                                  : "amount";

                              setSelectedServices(
                                newSelectedServices,
                                false,
                                false,
                              );
                            }}
                          >
                            {selectedService.discountType === "amount"
                              ? returnCurrencySymbol({
                                  currency: calendarData.currency || "EUR",
                                })
                              : "%"}
                          </Button>
                        }
                      />
                    </div>
                  </div>

                  {selectedService.discountAmount &&
                  selectedService.discountAmount > 0 ? (
                    <p className="text-xs text-gray-500">
                      <Trans>Končna cena</Trans>:{" "}
                      {formatCurrency({
                        amount: discountedPrice ?? 0,
                        currency: calendarData.currency || "EUR",
                        locale: i18n.locale,
                      })}
                    </p>
                  ) : undefined}
                </>
              )}

              {startDuration != null ||
              timeOffDuration != null ||
              endDuration != null ? (
                <>
                  <Divider />

                  {hasOnlyOneInput ? undefined : (
                    <p className="text-xs font-medium text-gray-500">
                      <Trans>Prosti čas med terminom</Trans>
                    </p>
                  )}

                  {startDuration != null && (
                    <>
                      {showComplexDurationPicker ? (
                        <DurationInput
                          value={selectedService.startDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].startDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          label={
                            hasOnlyOneInput ? t`Čas trajanja` : t`Začetni čas`
                          }
                          isDisabled={
                            !!permissions.restrict_custom_duration ||
                            !permissions.manage_appointments
                          }
                        />
                      ) : (
                        <LimeStepperNumberInput
                          value={selectedService.startDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].startDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          step={5}
                          minValue={1}
                          className="w-full"
                          label={
                            hasOnlyOneInput ? t`Čas trajanja` : t`Začetni čas`
                          }
                          endContent={
                            <span className="text-xs text-gray-500">min</span>
                          }
                          size="sm"
                          isDisabled={
                            !!permissions.restrict_custom_duration ||
                            !permissions.manage_appointments
                          }
                        />
                      )}
                      {!isPreScheduled &&
                        currentServiceAddons.flatMap((addon) => {
                          if (
                            !(
                              addon.durationMinsAdjustment > 0 &&
                              (addon.durationAdjustmentPosition === "start" ||
                                hasOnlyOneInput)
                            )
                          )
                            return [];
                          return [
                            <div
                              key={addon.id}
                              className="flex items-center justify-between rounded bg-gray-100 px-2 py-1 text-xs"
                            >
                              <span className="text-gray-600">
                                {addon.name}
                              </span>
                              <span className="font-medium text-gray-700">
                                +
                                {humanizeDuration(
                                  addon.durationMinsAdjustment * 60000,
                                )}
                              </span>
                            </div>,
                          ];
                        })}
                    </>
                  )}

                  {timeOffDuration != null && (
                    <>
                      {showComplexDurationPicker ? (
                        <DurationInput
                          value={selectedService.timeOffDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].timeOffDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          label={t`Čas brez zaposlenega`}
                          size={"sm"}
                          isDisabled={!!permissions.restrict_custom_duration}
                        />
                      ) : (
                        <LimeStepperNumberInput
                          value={selectedService.timeOffDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].timeOffDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          step={5}
                          minValue={1}
                          className="w-full"
                          label={t`Čas brez zaposlenega`}
                          size={"sm"}
                          isDisabled={!!permissions.restrict_custom_duration}
                        />
                      )}
                    </>
                  )}

                  {endDuration != null && (
                    <>
                      {showComplexDurationPicker ? (
                        <DurationInput
                          value={selectedService.endDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].endDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          label={
                            hasOnlyOneInput ? t`Čas trajanja` : t`Končni čas`
                          }
                          size={"sm"}
                          isDisabled={!!permissions.restrict_custom_duration}
                        />
                      ) : (
                        <LimeStepperNumberInput
                          value={selectedService.endDuration}
                          onValueChange={(val) => {
                            const newSelectedServices = [...selectedServices];
                            newSelectedServices[index].endDuration = val;
                            setSelectedServices(
                              newSelectedServices,
                              service.isExtraTimeAllowed ? true : false,
                              false,
                            );
                          }}
                          step={5}
                          // minValue={1}
                          className="w-full"
                          label={
                            hasOnlyOneInput ? t`Čas trajanja` : t`Končni čas`
                          }
                          size={"sm"}
                        />
                      )}
                      {!isPreScheduled &&
                        currentServiceAddons.flatMap((addon) => {
                          if (
                            !(
                              addon.durationMinsAdjustment > 0 &&
                              addon.durationAdjustmentPosition === "end"
                            )
                          )
                            return [];
                          return [
                            <div
                              key={addon.id}
                              className="flex items-center justify-between rounded bg-gray-100 px-2 py-1 text-xs"
                            >
                              <span className="text-gray-600">
                                {addon.name}
                              </span>
                              <span className="font-medium text-gray-700">
                                +
                                {humanizeDuration(
                                  addon.durationMinsAdjustment * 60000,
                                )}
                              </span>
                            </div>,
                          ];
                        })}
                    </>
                  )}
                </>
              ) : undefined}
            </>

            {isFeatureEnabled("serviceBlockBeforeAfter") && (
              <>
                <Divider />
                <p className="text-xs font-medium text-gray-500">
                  <Trans>Blokiranje časa</Trans>
                </p>
                <div className="flex flex-row gap-2 sm:flex-col sm:gap-1">
                  <LimeStepperNumberInput
                    value={selectedService.blockBeforeMins}
                    onValueChange={(val) => {
                      const newSelectedServices = [...selectedServices];
                      newSelectedServices[index].blockBeforeMins = val;
                      setSelectedServices(newSelectedServices);
                    }}
                    step={5}
                    min={0}
                    label={t`Pred terminom`}
                    endContent={
                      <span className="text-xs text-gray-500">min</span>
                    }
                    size={"sm"}
                  />

                  <LimeStepperNumberInput
                    value={selectedService.blockAfterMins}
                    onValueChange={(val) => {
                      const newSelectedServices = [...selectedServices];
                      newSelectedServices[index].blockAfterMins = val;
                      setSelectedServices(newSelectedServices);
                    }}
                    step={5}
                    min={0}
                    label={t`Po terminu`}
                    endContent={
                      <span className="text-xs text-gray-500">min</span>
                    }
                    size={"sm"}
                  />
                </div>
              </>
            )}

            {selectedServices.length > 1 && (
              <Button
                onPress={removeServiceFromLinked}
                variant={"light"}
                color="danger"
                size="sm"
              >
                <Trans>Odstrani storitev</Trans>
                <Trash size={16} />
              </Button>
            )}
          </div>
        </LimeCollapse>
      </div>
    </div>
  );
};

export default SelectedServiceListItem;
